import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { 
  BookOpen, 
  FileText, 
  ClipboardCheck,
  Target,
  Layers,
  CheckCircle2,
  Printer,
  GraduationCap,
  Search,
  MapPin,
  Globe,
  Swords,
  Flag,
  Landmark,
  Sparkles,
  PenTool,
  Clock,
  BookMarked
} from 'lucide-react';

import { historyCurriculum } from '@/data/historyCurriculum';
import { historyWeeklyLessons } from '@/data/historyWeeklyLessons';
import LessonPlanGenerator from '@/components/curriculum/LessonPlanGenerator';
import SubjectSchemeOfWork from '@/components/curriculum/SubjectSchemeOfWork';
import TermExamGenerator from '@/components/curriculum/TermExamGenerator';
import QuickPrintAll from '@/components/curriculum/QuickPrintAll';
import { FileQuestion, Files } from 'lucide-react';
import { getCurrentUser } from '@/data/users';

export default function HistoryCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 11");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [teacherYears, setTeacherYears] = useState(['11', '12', '13']);
  
  useEffect(() => {
    const currentUser = getCurrentUser();
    if (currentUser?.classes) {
      const years = currentUser.classes
        .filter(c => c.toLowerCase().includes('history'))
        .map(c => {
          const match = c.match(/year\s*(\d+)/i);
          return match ? match[1] : null;
        })
        .filter(Boolean);
      if (years.length > 0) {
        setTeacherYears([...new Set(years)]);
      }
    }
  }, []);

  const yearKey = selectedYear.toLowerCase().replace(' ', '');
  const curriculum = historyCurriculum[yearKey] || historyCurriculum.year11;

  const getIconComponent = (iconName) => {
    const icons = {
      Search: Search,
      MapPin: MapPin,
      Globe: Globe,
      Swords: Swords,
      Flag: Flag,
      Landmark: Landmark,
      BookOpen: BookOpen,
      FileText: FileText
    };
    return icons[iconName] || BookOpen;
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800' },
      green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-800' },
      purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-800' },
      red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-800' },
      orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800' },
      teal: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', badge: 'bg-teal-100 text-teal-800' }
    };
    return colors[color] || colors.blue;
  };

  const getSkillLevelBadge = (level) => {
    const levels = {
      1: { label: 'Uni-Structural', color: 'bg-blue-100 text-blue-800' },
      2: { label: 'Multi-Structural', color: 'bg-green-100 text-green-800' },
      3: { label: 'Relational', color: 'bg-purple-100 text-purple-800' },
      4: { label: 'Extended Abstract', color: 'bg-orange-100 text-orange-800' }
    };
    const levelInfo = levels[level] || levels[1];
    return <Badge className={levelInfo.color}>{levelInfo.label}</Badge>;
  };

  const totalTopics = curriculum.strands.reduce((acc, strand) => {
    return acc + strand.subStrands.reduce((subAcc, subStrand) => {
      return subAcc + (subStrand.topics?.length || 0);
    }, 0);
  }, 0);

  const totalSubStrands = curriculum.strands.reduce((acc, strand) => {
    return acc + (strand.subStrands?.length || 0);
  }, 0);

  return (
    <div className="p-6 space-y-6" data-testid="history-curriculum-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <BookMarked className="h-8 w-8 text-amber-600" />
            History Curriculum
          </h1>
          <p className="text-gray-600 mt-1">
            Senior Secondary (Years 11-13) - Vanuatu National Syllabus
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[140px]" data-testid="year-selector">
              <SelectValue placeholder="Select Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Year 11">Year 11</SelectItem>
              <SelectItem value="Year 12">Year 12</SelectItem>
              <SelectItem value="Year 13">Year 13</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{curriculum.hours}</p>
              <p className="text-sm text-gray-600">Total Hours</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Layers className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{curriculum.strands.length}</p>
              <p className="text-sm text-gray-600">Strands</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Target className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-700">{totalSubStrands}</p>
              <p className="text-sm text-gray-600">Sub-Strands</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-white">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <BookOpen className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">{totalTopics}</p>
              <p className="text-sm text-gray-600">Topics</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-5 w-full md:w-[600px]">
          <TabsTrigger value="curriculum" className="flex items-center gap-2" data-testid="curriculum-tab">
            <BookOpen className="h-4 w-4" />
            <span className="hidden sm:inline">Curriculum</span>
          </TabsTrigger>
          <TabsTrigger value="schemes" className="flex items-center gap-2" data-testid="schemes-tab">
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">Schemes</span>
          </TabsTrigger>
          <TabsTrigger value="strands" className="flex items-center gap-2" data-testid="strands-tab">
            <Layers className="h-4 w-4" />
            <span className="hidden sm:inline">Strands</span>
          </TabsTrigger>
          <TabsTrigger value="term-exam" className="flex items-center gap-2 bg-rose-100" data-testid="term-exam-tab">
            <FileQuestion className="h-4 w-4" />
            <span className="hidden sm:inline">Term Exam</span>
          </TabsTrigger>
          <TabsTrigger value="quick-print" className="flex items-center gap-2 bg-blue-100" data-testid="quick-print-tab">
            <Files className="h-4 w-4" />
            <span className="hidden sm:inline">Quick Print</span>
          </TabsTrigger>
          <TabsTrigger value="lesson-plans" className="flex items-center gap-2" data-testid="lesson-plans-tab">
            <PenTool className="h-4 w-4" />
            <span className="hidden sm:inline">Lesson Plans</span>
          </TabsTrigger>
          <TabsTrigger value="ai-generator" className="flex items-center gap-2" data-testid="ai-generator-tab">
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">AI Generator</span>
          </TabsTrigger>
        </TabsList>

        {/* Term Exam Tab */}
        <TabsContent value="term-exam" className="space-y-4">
          <TermExamGenerator
            subject="History"
            year={parseInt(selectedYear.replace('Year ', ''))}
            unitTests={[]}
            subjectColor="amber"
          />
        </TabsContent>

        {/* Quick Print Tab */}
        <TabsContent value="quick-print" className="space-y-4">
          <QuickPrintAll
            subject="History"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={historyWeeklyLessons}
            subjectColor="amber"
          />
        </TabsContent>

        {/* Schemes Tab - Weekly Scheme of Work */}
        <TabsContent value="schemes" className="space-y-4">
          <SubjectSchemeOfWork
            subject="History"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={historyWeeklyLessons}
            subjectColor="amber"
          />
        </TabsContent>

        {/* Curriculum Tab */}
        <TabsContent value="curriculum" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-amber-600" />
                {curriculum.title}
              </CardTitle>
              <CardDescription>{curriculum.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" className="space-y-4">
                {curriculum.strands.map((strand, strandIndex) => {
                  const IconComponent = getIconComponent(strand.icon);
                  const colorClasses = getColorClasses(strand.color);
                  
                  return (
                    <AccordionItem 
                      key={strand.id} 
                      value={strand.id}
                      className={`border rounded-lg ${colorClasses.border}`}
                    >
                      <AccordionTrigger className={`px-4 hover:no-underline ${colorClasses.bg} rounded-t-lg`}>
                        <div className="flex items-center gap-3 text-left">
                          <div className={`p-2 rounded-lg ${colorClasses.badge}`}>
                            <IconComponent className="h-5 w-5" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{strand.name}</h3>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Badge variant="outline">{strand.hours} hours</Badge>
                              <Badge variant="outline">{strand.weight}%</Badge>
                              <Badge variant="outline">{strand.subStrands.length} sub-strands</Badge>
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <p className="text-gray-600 mb-4 italic">{strand.outcome}</p>
                        
                        <div className="space-y-4">
                          {strand.subStrands.map((subStrand, subIndex) => (
                            <div key={subStrand.id} className="border rounded-lg p-4 bg-white">
                              <h4 className="font-medium text-gray-800 mb-2">{subStrand.name}</h4>
                              <p className="text-sm text-gray-600 mb-3">{subStrand.outcome}</p>
                              
                              {subStrand.topics && subStrand.topics.length > 0 && (
                                <div className="space-y-2">
                                  <h5 className="text-sm font-medium text-gray-700">Learning Outcomes:</h5>
                                  <div className="grid gap-2">
                                    {subStrand.topics.map((topic, topicIndex) => (
                                      <div 
                                        key={topic.code} 
                                        className="flex items-start gap-3 p-2 bg-gray-50 rounded-lg"
                                      >
                                        <Badge variant="outline" className="text-xs font-mono shrink-0">
                                          {topic.code}
                                        </Badge>
                                        <span className="text-sm text-gray-700 flex-1">{topic.topic}</span>
                                        {getSkillLevelBadge(topic.level)}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Strands Tab */}
        <TabsContent value="strands" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {curriculum.strands.map((strand) => {
              const IconComponent = getIconComponent(strand.icon);
              const colorClasses = getColorClasses(strand.color);
              const topicsCount = strand.subStrands.reduce((acc, sub) => acc + (sub.topics?.length || 0), 0);
              
              return (
                <Card key={strand.id} className={`${colorClasses.bg} ${colorClasses.border} border`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-xl ${colorClasses.badge}`}>
                        <IconComponent className="h-6 w-6" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{strand.name}</CardTitle>
                        <p className="text-sm text-gray-600">{strand.hours} hours ({strand.weight}%)</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">{strand.outcome}</p>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Sub-strands</span>
                        <span className="font-medium">{strand.subStrands.length}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Topics</span>
                        <span className="font-medium">{topicsCount}</span>
                      </div>
                      <Progress value={strand.weight} className="h-2 mt-2" />
                    </div>

                    <div className="mt-4 space-y-1">
                      {strand.subStrands.map((sub) => (
                        <div key={sub.id} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span className="text-gray-700 truncate">{sub.name}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Lesson Plans Tab */}
        <TabsContent value="lesson-plans" className="space-y-4">
          <LessonPlanGenerator 
            subject="History"
            subjectColor="amber"
            defaultYear={parseInt(selectedYear.replace('Year ', ''))}
            allowedYears={teacherYears}
            weeklyLessons={historyWeeklyLessons}
          />
        </TabsContent>

        {/* AI Generator Tab */}
        <TabsContent value="ai-generator" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                AI Lesson Plan Generator
              </CardTitle>
              <CardDescription>
                Generate customized lesson plans for History using AI
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-medium text-amber-800 mb-2">Available Strands for {selectedYear}:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {curriculum.strands.map((strand) => (
                      <div key={strand.id} className="flex items-center gap-2 text-sm">
                        <Badge className={getColorClasses(strand.color).badge}>
                          {strand.weight}%
                        </Badge>
                        <span className="text-gray-700">{strand.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-800 mb-2">Key Focus Areas:</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <Search className="h-4 w-4 text-blue-500" />
                      Skills of History - Research methods and source analysis
                    </li>
                    <li className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-green-500" />
                      Early History of Vanuatu - Origins and traditional societies
                    </li>
                    <li className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-purple-500" />
                      Imperialism & Colonialism - Pacific impacts
                    </li>
                    <li className="flex items-center gap-2">
                      <Swords className="h-4 w-4 text-red-500" />
                      Interwar Period - WWI, WWII, and Pacific involvement
                    </li>
                    <li className="flex items-center gap-2">
                      <Flag className="h-4 w-4 text-orange-500" />
                      Decolonization - Path to independence
                    </li>
                    <li className="flex items-center gap-2">
                      <Landmark className="h-4 w-4 text-teal-500" />
                      Independence to Present - Vanuatu's journey
                    </li>
                  </ul>
                </div>

                <Button className="w-full bg-amber-600 hover:bg-amber-700">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate AI Lesson Plan
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
